import React, { useState, useEffect, useMemo } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { Column, Button, Modal, Input, Label, Badge, Card } from '@/components/ui';
import { ResponsiveTable as DataTable } from '@/components/ui/responsive-table';
import { ModemCapabilitiesEditor } from '@/components/admin/edge/ModemCapabilitiesEditor';
import {
  ModemCapabilities,
  ModemModel,
  createDefaultCapabilities,
  normalizeCapabilities,
  getCapabilityValue,
} from '@simcheck/shared-types';
import { Plus, Edit, Trash2, Wifi, Radio, Zap, Smartphone, Activity, Factory, Hash, FileText, ToggleLeft, Database } from 'lucide-react';
import { useDataTableFilters, DataTableFilter, createColumnConfigHelper, applyFilters } from '@/components/data-table-filter';
import { useAuth } from '@/contexts/AuthContext';
import * as modemModelService from '@/services/modemModelService';

// Filter column configuration for DataTableFilter
const filterColumnHelper = createColumnConfigHelper<ModemModel>();

// Static filter configs
const getStaticFilterConfigs = () => [
  filterColumnHelper
    .text()
    .id('model')
    .accessor((row) => row.model)
    .displayName('Model')
    .icon(Hash)
    .build(),
  filterColumnHelper
    .text()
    .id('description')
    .accessor((row) => row.description || '')
    .displayName('Description')
    .icon(FileText)
    .build(),
  filterColumnHelper
    .option()
    .id('isActive')
    .accessor((row) => row.isActive ? 'active' : 'inactive')
    .displayName('Status')
    .icon(ToggleLeft)
    .options([
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
    ])
    .build(),
];

export default function ModemModelsPage() {
  // Modem Models is a globally-shared catalogue. The page is mounted at
  // /admin/modem-models (super-admin) AND /edges/modems (any authenticated
  // user — read-only). Gate every write affordance on isSuperAdmin so the
  // tenant-facing mount is automatically read-only without needing a
  // separate component or scope prop.
  const { user, isSuperAdmin } = useAuth();
  const [modemModels, setModemModels] = useState<ModemModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingModel, setEditingModel] = useState<ModemModel | null>(null);
  const [formData, setFormData] = useState({
    manufacturer: '',
    model: '',
    description: '',
    capabilities: createDefaultCapabilities(),
    isActive: true,
  });

  // Build dynamic filter config with manufacturer options and capability filters
  const filterColumnsConfig = useMemo(() => {
    // Get unique manufacturers from modem models
    const uniqueManufacturers = [...new Set(modemModels.map(m => m.manufacturer).filter(Boolean))].sort();

    return [
      filterColumnHelper
        .option()
        .id('manufacturer')
        .accessor((row) => row.manufacturer)
        .displayName('Manufacturer')
        .icon(Factory)
        .options(uniqueManufacturers.map(m => ({ label: m, value: m })))
        .build(),
      ...getStaticFilterConfigs(),
      // Capability filters
      filterColumnHelper
        .option()
        .id('supports5G')
        .accessor((row) => getCapabilityValue(normalizeCapabilities(row.capabilities), 'supports5G') ? 'yes' : 'no')
        .displayName('5G')
        .icon(Wifi)
        .options([
          { label: 'Supports 5G', value: 'yes' },
          { label: 'No 5G', value: 'no' },
        ])
        .build(),
      filterColumnHelper
        .option()
        .id('supports4G')
        .accessor((row) => getCapabilityValue(normalizeCapabilities(row.capabilities), 'supports4G') ? 'yes' : 'no')
        .displayName('4G')
        .icon(Radio)
        .options([
          { label: 'Supports 4G', value: 'yes' },
          { label: 'No 4G', value: 'no' },
        ])
        .build(),
      filterColumnHelper
        .option()
        .id('supports3G')
        .accessor((row) => getCapabilityValue(normalizeCapabilities(row.capabilities), 'supports3G') ? 'yes' : 'no')
        .displayName('3G')
        .icon(Radio)
        .options([
          { label: 'Supports 3G', value: 'yes' },
          { label: 'No 3G', value: 'no' },
        ])
        .build(),
      filterColumnHelper
        .option()
        .id('supports2G')
        .accessor((row) => getCapabilityValue(normalizeCapabilities(row.capabilities), 'supports2G') ? 'yes' : 'no')
        .displayName('2G')
        .icon(Radio)
        .options([
          { label: 'Supports 2G', value: 'yes' },
          { label: 'No 2G', value: 'no' },
        ])
        .build(),
      filterColumnHelper
        .option()
        .id('supportsVoLTE')
        .accessor((row) => getCapabilityValue(normalizeCapabilities(row.capabilities), 'supportsVoLTE') ? 'yes' : 'no')
        .displayName('VoLTE')
        .icon(Zap)
        .options([
          { label: 'Supports VoLTE', value: 'yes' },
          { label: 'No VoLTE', value: 'no' },
        ])
        .build(),
      filterColumnHelper
        .option()
        .id('supportseSIM')
        .accessor((row) => getCapabilityValue(normalizeCapabilities(row.capabilities), 'supportseSIM') ? 'yes' : 'no')
        .displayName('eSIM')
        .icon(Smartphone)
        .options([
          { label: 'Supports eSIM', value: 'yes' },
          { label: 'No eSIM', value: 'no' },
        ])
        .build(),
      filterColumnHelper
        .option()
        .id('supportsLTEM')
        .accessor((row) => getCapabilityValue(normalizeCapabilities(row.capabilities), 'supportsLTEM') ? 'yes' : 'no')
        .displayName('LTE-M')
        .icon(Activity)
        .options([
          { label: 'Supports LTE-M', value: 'yes' },
          { label: 'No LTE-M', value: 'no' },
        ])
        .build(),
      filterColumnHelper
        .option()
        .id('supportsNBIoT')
        .accessor((row) => getCapabilityValue(normalizeCapabilities(row.capabilities), 'supportsNBIoT') ? 'yes' : 'no')
        .displayName('NB-IoT')
        .icon(Activity)
        .options([
          { label: 'Supports NB-IoT', value: 'yes' },
          { label: 'No NB-IoT', value: 'no' },
        ])
        .build(),
    ];
  }, [modemModels]);

  // DataTableFilter hook
  const { columns: filterColumns, filters, actions, strategy } = useDataTableFilters({
    strategy: 'client',
    data: modemModels,
    columnsConfig: filterColumnsConfig,
  });

  // Apply filters to modem models data
  const filteredModemModels = useMemo(() =>
    applyFilters(modemModels, filters, filterColumnsConfig),
    [modemModels, filters, filterColumnsConfig]
  );

  // Load modem models from Firestore
  const loadModemModels = async () => {
    setLoading(true);
    setError(null);
    try {
      const models = await modemModelService.getAllModemModels();
      setModemModels(models);
    } catch (err: any) {
      console.error('Error loading modem models:', err);
      setError(err.message || 'Failed to load modem models');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadModemModels();
  }, []);

  // Seed database with initial data
  const handleSeedDatabase = async () => {
    if (!user?.uid) return;
    setSaving(true);
    setError(null);
    try {
      const created = await modemModelService.seedModemModels(user.uid);
      if (created > 0) {
        await loadModemModels();
      }
    } catch (err: any) {
      console.error('Error seeding database:', err);
      setError(err.message || 'Failed to seed database');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      manufacturer: '',
      model: '',
      description: '',
      capabilities: createDefaultCapabilities(),
      isActive: true,
    });
  };

  const handleOpenModal = (model?: ModemModel) => {
    if (model) {
      setEditingModel(model);
      setFormData({
        manufacturer: model.manufacturer,
        model: model.model,
        description: model.description || '',
        capabilities: normalizeCapabilities(model.capabilities),
        isActive: model.isActive,
      });
    } else {
      setEditingModel(null);
      resetForm();
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingModel(null);
    resetForm();
  };

  const handleSave = async () => {
    if (!user?.uid) return;
    setSaving(true);
    setError(null);
    try {
      if (editingModel) {
        // Update existing
        await modemModelService.updateModemModel(editingModel.id, {
          manufacturer: formData.manufacturer,
          model: formData.model,
          description: formData.description,
          capabilities: formData.capabilities,
          isActive: formData.isActive,
        });
      } else {
        // Create new
        await modemModelService.createModemModel(user.uid, {
          manufacturer: formData.manufacturer,
          model: formData.model,
          description: formData.description,
          capabilities: formData.capabilities,
          isActive: formData.isActive,
        });
      }
      handleCloseModal();
      await loadModemModels();
    } catch (err: any) {
      console.error('Error saving modem model:', err);
      setError(err.message || 'Failed to save modem model');
    } finally {
      setSaving(false);
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; modelId: string | null }>({
    isOpen: false,
    modelId: null
  });

  const handleDelete = (modelId: string) => {
    setDeleteConfirm({ isOpen: true, modelId });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.modelId) return;
    setSaving(true);
    setError(null);
    try {
      await modemModelService.deleteModemModel(deleteConfirm.modelId);
      setDeleteConfirm({ isOpen: false, modelId: null });
      await loadModemModels();
    } catch (err: any) {
      console.error('Error deleting modem model:', err);
      setError(err.message || 'Failed to delete modem model');
    } finally {
      setSaving(false);
    }
  };

  const getCapabilityBadges = (capabilities: ModemCapabilities) => {
    const normalized = normalizeCapabilities(capabilities);
    const badges = [];

    // Network generations
    if (getCapabilityValue(normalized, 'supports5G')) {
      badges.push({ label: '5G', color: 'bg-tech-5g-bg text-tech-5g', icon: Wifi });
    }
    if (getCapabilityValue(normalized, 'supports4G')) {
      badges.push({ label: '4G', color: 'bg-status-pending-bg text-status-pending', icon: Radio });
    }
    if (getCapabilityValue(normalized, 'supports3G')) {
      badges.push({ label: '3G', color: 'bg-status-warning-bg text-status-warning', icon: Radio });
    }
    if (getCapabilityValue(normalized, 'supports2G')) {
      badges.push({ label: '2G', color: 'bg-status-offline-bg text-status-offline', icon: Radio });
    }

    // IoT (LPWA)
    if (getCapabilityValue(normalized, 'supportsLTEM')) {
      badges.push({ label: 'LTE-M', color: 'bg-tech-4g-bg text-tech-4g', icon: Activity });
    }
    if (getCapabilityValue(normalized, 'supportsNBIoT')) {
      badges.push({ label: 'NB-IoT', color: 'bg-tech-4g-bg text-tech-4g', icon: Activity });
    }

    // Features
    if (getCapabilityValue(normalized, 'supportsVoLTE')) {
      badges.push({ label: 'VoLTE', color: 'bg-status-success-bg text-status-success', icon: Zap });
    }
    if (getCapabilityValue(normalized, 'supportsVoNR')) {
      badges.push({ label: 'VoNR', color: 'bg-status-success-bg text-status-success', icon: Zap });
    }
    if (getCapabilityValue(normalized, 'supportseSIM')) {
      badges.push({ label: 'eSIM', color: 'bg-accent-orange/10 text-accent-orange', icon: Smartphone });
    }

    return badges;
  };

  const columns: Column<ModemModel>[] = [
    {
      key: 'manufacturer',
      header: 'Manufacturer',
      sortable: true,
      width: '15%',
      render: (_value: unknown, row: ModemModel) => (
        <div className="font-medium text-foreground">
          {row.manufacturer}
        </div>
      )
    },
    {
      key: 'model',
      header: 'Model',
      sortable: true,
      width: '15%',
      render: (_value: unknown, row: ModemModel) => (
        <code className="text-sm text-muted-foreground">
          {row.model}
        </code>
      )
    },
    {
      key: 'description',
      header: 'Description',
      sortable: false,
      width: '25%',
      render: (_value: unknown, row: ModemModel) => (
        <div className="text-sm text-muted-foreground truncate max-w-xs">
          {row.description || 'No description'}
        </div>
      )
    },
    {
      key: 'capabilities',
      header: 'Capabilities',
      sortable: false,
      width: '25%',
      render: (_value: unknown, row: ModemModel) => (
        <div className="flex flex-wrap gap-1">
          {getCapabilityBadges(row.capabilities).map((badge, index) => (
            <Badge key={index} className={`${badge.color} text-xs`}>
              <badge.icon className="w-3 h-3 mr-1" />
              {badge.label}
            </Badge>
          ))}
        </div>
      )
    },
    {
      key: 'isActive',
      header: 'Status',
      sortable: true,
      width: '10%',
      render: (_value: unknown, row: ModemModel) => (
        <Badge className={row.isActive ? 'bg-status-success-bg text-status-success' : 'bg-status-offline-bg text-status-offline'}>
          {row.isActive ? 'Active' : 'Inactive'}
        </Badge>
      )
    },
    // Per-row Edit/Delete actions: super-admin only. Tenants viewing the
    // page at /edges/modems get a read-only catalogue.
    ...(isSuperAdmin ? [{
      key: 'actions',
      header: 'Actions',
      sortable: false,
      width: '10%',
      render: (_value: unknown, row: ModemModel) => (
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleOpenModal(row)}
            className="h-8 w-8 p-0"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(row.id)}
            className="h-8 w-8 p-0 text-status-error hover:text-status-error/80"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )
    }] : [])
  ];

  return (
    <AppShell>
      <div className="p-6">
        <div className="mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                Modem Models Database
              </h1>
              <p className="text-muted-foreground mt-1">
                Select a supported modem model for your edge devices
              </p>
            </div>
            {/* Header write affordances: super-admin only. */}
            {isSuperAdmin && (
              <div className="flex gap-2">
                {modemModels.length === 0 && !loading && (
                  <Button
                    variant="outline"
                    onClick={handleSeedDatabase}
                    disabled={saving}
                  >
                    <Database className="h-4 w-4 mr-2" />
                    {saving ? 'Seeding...' : 'Seed Initial Data'}
                  </Button>
                )}
                <Button onClick={() => handleOpenModal()} className="bg-accent-blue hover:bg-accent-blue/80">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Model
                </Button>
              </div>
            )}
          </div>

          {error && (
            <div className="mt-4 p-3 bg-status-error-bg border border-status-error/30 rounded-md text-status-error text-sm">
              {error}
            </div>
          )}
        </div>

        {/* DataTableFilter */}
        <Card className="p-4 mb-4">
          <DataTableFilter
            columns={filterColumns}
            filters={filters}
            actions={actions}
            strategy={strategy}
          />
        </Card>

        <DataTable
          columns={columns}
          data={filteredModemModels}
          keyExtractor={(row) => row.id}
          emptyMessage="No modem models found"
          loading={loading}
          paginated
          defaultPageSize={10}
          searchable
          searchPlaceholder="Search modem models by manufacturer or model..."
          onRowClick={(row) => handleOpenModal(row)}
        />

        {/* Add/Edit Modal — read-only for non-super-admins (tenants viewing /edges/modems). */}
        <Modal
          isOpen={modalOpen}
          onClose={handleCloseModal}
          title={
            isSuperAdmin
              ? (editingModel ? 'Edit Modem Model' : 'Add Modem Model')
              : 'Modem Model Details'
          }
          size="lg"
        >
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="manufacturer">Manufacturer</Label>
                <Input
                  id="manufacturer"
                  value={formData.manufacturer}
                  onChange={(e) => setFormData(prev => ({ ...prev, manufacturer: e.target.value }))}
                  placeholder="e.g., Quectel"
                  disabled={!isSuperAdmin}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="model">Model</Label>
                <Input
                  id="model"
                  value={formData.model}
                  onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                  placeholder="e.g., EG95"
                  disabled={!isSuperAdmin}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of the modem"
                disabled={!isSuperAdmin}
              />
            </div>

            {/* Capabilities Editor - Dynamic Form */}
            <ModemCapabilitiesEditor
              capabilities={formData.capabilities}
              onChange={(capabilities) => setFormData(prev => ({ ...prev, capabilities }))}
              readOnly={!isSuperAdmin}
            />

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={handleCloseModal} disabled={saving}>
                {isSuperAdmin ? 'Cancel' : 'Close'}
              </Button>
              {isSuperAdmin && (
                <Button onClick={handleSave} disabled={saving} className="bg-accent-blue hover:bg-accent-blue/80">
                  {saving ? 'Saving...' : (editingModel ? 'Update Model' : 'Add Model')}
                </Button>
              )}
            </div>
          </div>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={deleteConfirm.isOpen}
          onClose={() => !saving && setDeleteConfirm({ isOpen: false, modelId: null })}
          title="Delete Modem Model"
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Are you sure you want to delete this modem model? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm({ isOpen: false, modelId: null })}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                disabled={saving}
                className="bg-status-error hover:bg-status-error/80 text-white"
              >
                {saving ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </AppShell>
  );
}

